import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Truck, ShieldCheck, Award, RotateCcw, Headphones, Sparkles, Quote,
} from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { StarRating } from "@/components/StarRating";
import { formatPrice } from "@/lib/utils";

const HERO_IMG = "/products/corner-anton-velvet-u.jpg";
const PROMO_IMG = "/products/corner-karo-velvet.jpg";

const FEATURES = [
  { icon: Truck, title: "Livraison rapide", desc: "Livraison gant blanc et installation dans la pièce de votre choix." },
  { icon: ShieldCheck, title: "Paiement sécurisé", desc: "Transactions cryptées et protection totale de vos données." },
  { icon: Award, title: "Garantie qualité", desc: "Garantie décennale sur toutes nos structures et assemblages." },
  { icon: RotateCcw, title: "Retours simples", desc: "30 jours pour changer d'avis, retour gratuit et sans condition." },
  { icon: Headphones, title: "Service client", desc: "Une équipe de conseillers déco disponible 7j/7 pour vous guider." },
];

const REVIEWS = [
  { name: "Éléonore de V.", city: "Paris", rating: 5, avatar: "https://i.pravatar.cc/120?img=45", text: "Un canapé qui a complètement métamorphosé notre séjour. La qualité du tissu bouclé et le maintien de l'assise dépassent toutes nos attentes." },
  { name: "Julien M.", city: "Lyon", rating: 5, avatar: "https://i.pravatar.cc/120?img=12", text: "Service impeccable, livraison à l'heure et installation soignée. Le canapé d'angle Céleste est encore plus beau qu'en photo." },
  { name: "Sofia R.", city: "Bordeaux", rating: 5, avatar: "https://i.pravatar.cc/120?img=32", text: "Confort exceptionnel et finitions haut de gamme. On se croirait dans un showroom de luxe. Je recommande les yeux fermés." },
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products", { params: { sort: "bestsellers" } }).then((r) => setPopular(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative -mt-[104px] h-[100svh] min-h-[640px] w-full overflow-hidden">
        <motion.img
          src={HERO_IMG}
          alt="Salon moderne avec canapé haut de gamme"
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-obsidian/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-obsidian/30" />

        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-center px-6 pt-[104px] lg:px-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}
            className="mb-4 flex items-center gap-2 font-accent text-xs uppercase tracking-[0.3em] text-gold"
          >
            <Sparkles size={14} /> Maison française · Fabrication artisanale
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.9 }}
            className="max-w-3xl font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl lg:text-7xl"
          >
            Le confort qui transforme votre intérieur
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.8 }}
            className="mt-6 max-w-xl text-base text-white/80 lg:text-lg"
          >
            Découvrez notre collection exclusive de canapés haut de gamme, pensés pour l'élégance contemporaine et façonnés à la main.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/canapes"
              data-testid="hero-discover-button"
              className="group flex items-center gap-2 rounded-full bg-gold-gradient px-8 py-4 text-sm font-semibold text-obsidian shadow-luxe transition-transform hover:scale-[1.04]"
            >
              Découvrir nos canapés
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/canapes?sort=newest"
              data-testid="hero-new-button"
              className="rounded-full border border-white/40 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur-md transition-colors hover:bg-white hover:text-obsidian"
            >
              Voir les nouveautés
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60"
        >
          <div className="h-10 w-6 rounded-full border border-white/40 p-1.5">
            <motion.div className="h-2 w-2 rounded-full bg-gold" animate={{ y: [0, 12, 0] }} transition={{ repeat: Infinity, duration: 1.6 }} />
          </div>
        </motion.div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8" id="categories">
        <Reveal className="mb-12 text-center">
          <p className="mb-3 font-accent text-xs uppercase tracking-[0.25em] text-gold-dark">Nos collections</p>
          <h2 className="font-serif text-3xl font-medium sm:text-4xl lg:text-5xl">Explorez par catégorie</h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <Link
                to={`/canapes?category=${c.id}`}
                data-testid={`category-card-${c.id}`}
                className="group relative block h-[300px] overflow-hidden rounded-3xl shadow-soft lg:h-[380px]"
              >
                <img src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/85 via-obsidian/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <p className="text-xs uppercase tracking-widest text-gold">{c.count}</p>
                  <h3 className="mt-1 font-serif text-3xl text-white">{c.name}</h3>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm text-white/0 transition-all duration-300 group-hover:text-white">
                    Découvrir <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* POPULAR */}
      <section className="bg-secondary/40 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-3 font-accent text-xs uppercase tracking-[0.25em] text-gold-dark">Best-sellers</p>
              <h2 className="font-serif text-3xl font-medium sm:text-4xl lg:text-5xl">Produits populaires</h2>
            </div>
            <Link to="/canapes" className="group flex items-center gap-2 text-sm font-semibold text-foreground hover:text-gold-dark" data-testid="see-all-products">
              Voir tout <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
            {popular.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* PROMO */}
      <section className="relative overflow-hidden bg-obsidian py-4">
        <div className="mx-auto grid max-w-7xl items-stretch gap-0 px-6 py-16 lg:grid-cols-2 lg:gap-14 lg:px-8">
          <Reveal className="flex flex-col justify-center">
            <p className="mb-3 font-accent text-xs uppercase tracking-[0.3em] text-gold">Service premium inclus</p>
            <h2 className="font-serif text-4xl font-medium leading-tight text-cream lg:text-6xl">
              Livraison <span className="text-gold-gradient">offerte</span><br />& installation<br />gant blanc
            </h2>
            <p className="mt-6 max-w-md text-cream/60">
              Dès 1500€ d'achat, nous livrons et installons votre canapé dans la pièce de votre choix. Nos lits sont livrés avec le surmatelas (topper) offert.
            </p>
            <Link
              to="/canapes"
              data-testid="promo-cta-button"
              className="group mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-gold-gradient px-8 py-4 text-sm font-semibold text-obsidian transition-transform hover:scale-[1.04]"
            >
              Découvrir la collection <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="animate-float-slow overflow-hidden rounded-[2rem] shadow-luxe">
              <img src={PROMO_IMG} alt="Canapé d'angle KARO Velvet" className="h-full min-h-[320px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-2xl bg-cream px-6 py-4 shadow-luxe">
              <p className="font-serif text-3xl text-obsidian">0€</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Livraison</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <Reveal className="mb-14 text-center">
          <p className="mb-3 font-accent text-xs uppercase tracking-[0.25em] text-gold-dark">L'expérience shop-canapé</p>
          <h2 className="font-serif text-3xl font-medium sm:text-4xl lg:text-5xl">Pourquoi nous choisir ?</h2>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="group h-full rounded-3xl border border-border bg-card p-7 text-center shadow-soft transition-all duration-500 hover:-translate-y-2 hover:shadow-luxe">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary text-gold-dark transition-colors group-hover:bg-gold group-hover:text-obsidian">
                  <f.icon size={24} />
                </div>
                <h3 className="font-serif text-xl">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bg-secondary/40 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal className="mb-14 text-center">
            <p className="mb-3 font-accent text-xs uppercase tracking-[0.25em] text-gold-dark">Ils nous font confiance</p>
            <h2 className="font-serif text-3xl font-medium sm:text-4xl lg:text-5xl">Avis de nos clients</h2>
          </Reveal>
          <div className="grid gap-6 lg:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.1}>
                <div className="relative h-full rounded-3xl bg-card p-8 shadow-soft" data-testid={`review-card-${i}`}>
                  <Quote size={40} className="mb-4 text-gold/30" />
                  <p className="text-foreground/90">{r.text}</p>
                  <div className="mt-6 flex items-center gap-3">
                    <img src={r.avatar} alt={r.name} className="h-12 w-12 rounded-full object-cover ring-2 ring-gold/30" />
                    <div className="flex-1">
                      <p className="font-serif text-lg leading-tight">{r.name}</p>
                      <p className="text-xs text-muted-foreground">{r.city}, France</p>
                    </div>
                    <StarRating rating={r.rating} size={14} />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
